import { Link } from 'expo-router';
import ArrowRight from 'lucide-react-native/icons/arrow-right';
import Calendar from 'lucide-react-native/icons/calendar';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { useTranslation } from 'react-i18next';

import { CardImage } from '@/components/riviera/card-image';
import { Badge } from '@/components/ui/badge';
import { Fonts, Radius, Spacing } from '@/constants/theme';
import { useTheme } from '@/hooks/use-theme';
import { articleImageAlt } from '@/lib/i18n/article-content';
import { formatDateShort } from '@todo/core/i18n/format';
import type { Locale } from '@todo/core/i18n/locales';
import { urlForImage } from '@todo/core/sanity/client';
import type { Article } from '@todo/core/sanity/queries';

// Fixed-size article card shared by the articles section on native and web.
// No breakpoint logic (always 240px wide), so both platform variants import
// this one file.
export function ArticleCard({ article, locale }: { article: Article; locale: Locale }) {
  const theme = useTheme();
  const { t } = useTranslation();
  const imageUrl = article.mainImage
    ? urlForImage(article.mainImage).width(480).height(280).url()
    : null;

  return (
    <Link
      href={{ pathname: '/[lang]/articles/[slug]', params: { lang: locale, slug: article.slug } }}
      asChild>
      <Pressable style={({ pressed }) => [styles.cardPressable, pressed && { opacity: 0.85 }]}>
        <View style={[styles.card, { backgroundColor: theme.card, borderColor: theme.border }]}>
          <View style={styles.imageBox}>
            <CardImage uri={imageUrl} alt={articleImageAlt(article)} />
            <View style={styles.badgePos}>
              <Badge variant="gold">{t('articles.badge')}</Badge>
            </View>
          </View>
          <View style={styles.body}>
            <Text
              numberOfLines={2}
              style={[styles.title, { color: theme.text, fontFamily: Fonts?.serif }]}>
              {article.title}
            </Text>
            {article.publishedAt && (
              <View style={styles.metaRow}>
                <Calendar size={13} color={theme.mutedForeground} />
                <Text style={[styles.meta, { color: theme.mutedForeground }]}>
                  {formatDateShort(article.publishedAt, locale)}
                </Text>
              </View>
            )}
            <View style={styles.footerRow}>
              <Text style={[styles.readMoreText, { color: theme.primary }]}>
                {t('articles.readArticle')}
              </Text>
              <ArrowRight size={14} color={theme.primary} />
            </View>
          </View>
        </View>
      </Pressable>
    </Link>
  );
}

const styles = StyleSheet.create({
  cardPressable: {
    width: 240,
    flexGrow: 0,
    flexShrink: 0,
  },
  card: {
    width: 240,
    borderRadius: Radius.xl,
    borderWidth: 1,
    overflow: 'hidden',
  },
  imageBox: {
    height: 140,
    width: '100%',
    position: 'relative',
  },
  image: {
    width: '100%',
    height: '100%',
  },
  badgePos: {
    position: 'absolute',
    top: Spacing.two,
    left: Spacing.two,
  },
  body: {
    padding: Spacing.three - 2,
    gap: 4,
  },
  title: {
    fontSize: 15,
    fontWeight: '600',
    lineHeight: 20,
    minHeight: 40,
  },
  metaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  meta: {
    fontSize: 12,
  },
  footerRow: {
    marginTop: 4,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  readMoreText: {
    fontSize: 13,
    fontWeight: '600',
  },
});
